import { ScoringBand } from '@@types/api.types';
import { clampDashboardPercentage } from '@azymmetric/shared/types/meia/scoring.types';
import { TooltipComponent } from '@components/core/tooltip.component';
import { Badge, type BadgeProps } from '@components/ui/badge';
import { cn } from '@lib/utils';
import { type MeiaScoringConfigFormValues } from '@schemas/meia.schema';
import { type FC } from 'react';
import { formatDashboardScore } from './meia-p4-run-cases.util';
import { getScoringBandTone } from './meia-scoring-band.util';

export const clampPercentage = clampDashboardPercentage;

type InfoTooltipProps = {
  children: string;
};

export const InfoTooltip: FC<InfoTooltipProps> = (props) => {
  const { children } = props;

  return <TooltipComponent side="top">{children}</TooltipComponent>;
};

type ColumnLabelProps = {
  children: string;
  tooltip: string;
};

export const ColumnLabel: FC<ColumnLabelProps> = (props) => {
  const { children, tooltip } = props;

  return (
    <span className="inline-flex items-center justify-center gap-1">
      {children}
      <TooltipComponent side="top">{tooltip}</TooltipComponent>
    </span>
  );
};

type LegendSwatchProps = {
  colorClass: string;
  label: string;
};

export const LegendSwatch: FC<LegendSwatchProps> = (props) => {
  const { colorClass, label } = props;

  return (
    <span className="flex items-center gap-1">
      <span className={cn('size-3 rounded-sm', colorClass)} />
      {label}
    </span>
  );
};

type TruncatedMetricTextProps = {
  className?: string;
  text: string;
};

export const TruncatedMetricText: FC<TruncatedMetricTextProps> = (props) => {
  const { className, text } = props;

  return (
    <TooltipComponent
      className="max-w-[420px]"
      side="top"
      trigger={<span className={cn('block truncate', className)}>{text}</span>}
    >
      {text}
    </TooltipComponent>
  );
};

type ScorePillProps = {
  score?: number | null;
  scoringConfig: MeiaScoringConfigFormValues;
};

export const ScorePill: FC<ScorePillProps> = (props) => {
  const { score, scoringConfig } = props;

  return (
    <span
      className={cn(
        'inline-flex min-w-14 items-center justify-center rounded px-2 py-1 font-mono font-semibold',
        scorePillClass({ score, scoringConfig }),
      )}
    >
      {formatDashboardScore(score)}
    </span>
  );
};

type VerdictDotProps = {
  band?: ScoringBand | null;
};

export const VerdictDot: FC<VerdictDotProps> = (props) => {
  const { band } = props;

  return <span className={cn('inline-flex size-2.5 rounded-full', bandDotClass(band))} />;
};

type VerdictBandScaleProps = {
  thresholds: {
    greenThreshold: number;
    yellowThreshold: number;
  };
};

export const VerdictBandScale: FC<VerdictBandScaleProps> = (props) => {
  const { thresholds } = props;
  const greenThreshold = clampPercentage(thresholds.greenThreshold);
  const yellowThreshold = clampPercentage(thresholds.yellowThreshold);
  const redWidth = yellowThreshold;
  const yellowWidth = Math.max(greenThreshold - yellowThreshold, 0);
  const greenWidth = Math.max(100 - greenThreshold, 0);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex h-2 overflow-hidden rounded-sm">
        <div
          className="bg-red-600"
          style={{ width: `${redWidth}%` }}
        />
        <div
          className="bg-yellow-500"
          style={{ width: `${yellowWidth}%` }}
        />
        <div
          className="bg-green-600"
          style={{ width: `${greenWidth}%` }}
        />
      </div>
      <div className="relative h-4 font-mono text-[10px] text-gray-500">
        <span className="absolute left-0">0</span>
        <span
          className="absolute -translate-x-1/2"
          style={{ left: `${yellowThreshold}%` }}
        >
          {yellowThreshold}
        </span>
        <span
          className="absolute -translate-x-1/2"
          style={{ left: `${greenThreshold}%` }}
        >
          {greenThreshold}
        </span>
        <span className="absolute right-0">100</span>
      </div>
    </div>
  );
};

type DashboardStatTileProps = {
  band?: ScoringBand | null;
  label: string;
  numerator?: number;
  progressTone?: 'green' | 'red' | 'yellow';
  total?: number;
  value: string;
};

export const DashboardStatTile: FC<DashboardStatTileProps> = (props) => {
  const { band, label, numerator, progressTone, total, value } = props;
  const width =
    numerator !== undefined && total !== undefined && total > 0 && Number.isFinite(numerator)
      ? `${Math.round((numerator / total) * 100)}%`
      : '0%';

  return (
    <div className="rounded-md border border-gray-200 bg-white p-3">
      <div className="text-xs font-medium text-gray-500">{label}</div>
      <div className="mt-2 flex items-baseline gap-2">
        <DashboardStatValue
          className={statToneClass(progressTone, band)}
          value={value}
        />
        {band && <Badge variant={scoringBandVariant(band)}>{band}</Badge>}
      </div>
      {progressTone && (
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-100">
          <div
            className={cn('h-full rounded-full', progressToneClass(progressTone))}
            style={{ width }}
          />
        </div>
      )}
    </div>
  );
};

type DashboardStatValueProps = {
  className?: string;
  value: string;
};

const DashboardStatValue: FC<DashboardStatValueProps> = (props) => {
  const { className, value } = props;
  const [score, denominator] = value.split(' / ');

  if (!denominator) {
    return <span className={cn('text-2xl font-semibold', className)}>{value}</span>;
  }

  return (
    <span className={cn('inline-flex items-baseline gap-1 text-2xl font-semibold', className)}>
      <span>{score}</span>
      <span className="text-sm font-medium text-gray-500">/ {denominator}</span>
    </span>
  );
};

export const formatNullableScore = (score?: number | null): string => {
  if (score === null || score === undefined) return '-';
  return formatDashboardScore(score);
};

export const metricScoreClass = (band?: ScoringBand | null): string => getScoringBandTone(band).textClass;

export const scoringBandVariant = (band: ScoringBand): BadgeProps['variant'] => getScoringBandTone(band).badgeVariant;

const statToneClass = (progressTone?: 'green' | 'red' | 'yellow', band?: ScoringBand | null): string => {
  if (progressTone === 'green' || band === ScoringBand.GREEN) return 'text-green-700';
  if (progressTone === 'red' || band === ScoringBand.RED) return 'text-red-700';
  if (progressTone === 'yellow' || band === ScoringBand.YELLOW) return 'text-yellow-700';
  return 'text-gray-950';
};

const progressToneClass = (tone: 'green' | 'red' | 'yellow'): string => {
  switch (tone) {
    case 'green':
      return 'bg-green-600';
    case 'red':
      return 'bg-red-600';
    case 'yellow':
      return 'bg-yellow-500';
  }
};

const scorePillClass = (params: {
  score: number | null | undefined;
  scoringConfig: MeiaScoringConfigFormValues;
}): string => {
  const { score, scoringConfig } = params;
  if (score === null || score === undefined) return 'bg-gray-200 text-white';
  if (score >= scoringConfig.greenThreshold) return 'bg-green-600 text-white';
  if (score >= scoringConfig.yellowThreshold) return 'bg-yellow-500 text-gray-950';
  return 'bg-red-600 text-white';
};

const bandDotClass = (band?: ScoringBand | null): string => getScoringBandTone(band).dotClass;
